import React, { type Ref } from 'react';

import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { IconClose, IconSearch } from '@pkg/assets/icons';

import { FieldText, type FieldTextProps } from '../FieldText';
import { tokens as fieldTextTokens } from '../FieldText/FieldText.tokens';

import { ClearAction, ClearActionWrapper, ClearIconWrapper, SearchIconWrapper } from './FieldSearch.style';
import { tokens } from './FieldSearch.tokens';

// TODO: Cannot pass `label` and `size` until we
// fully support the various `size` layouts.
type FieldTextSubset = Pick<
    FieldTextProps,
    | 'inputRef'
    | 'popoverProps'
    | 'id'
    | 'name'
    | 'value'
    // | 'label'
    | 'placeholder'
    | 'suffix'
    | 'suffixOffset'
    // | 'size'
    | 'theme'
    | 'autoFocus'
    | 'backdropBlur'
    | 'disabled'
    | 'error'
    | 'readOnly'
    | 'required'
    | 'onBlur'
    | 'onFocus'
    | 'onChange'
>;

export interface FieldSearchProps extends FieldTextSubset {
    wrapperRef?: Ref<HTMLDivElement>;
}

const PREFIX_OFFSET = tokens.search.size + fieldTextTokens.gap.small;
const SUFFIX_OFFSET = tokens.clear.size + fieldTextTokens.gap.small;

export function FieldSearch({
    wrapperRef,
    value = '',
    placeholder,
    suffix,
    suffixOffset = 0,
    onChange,
    ...rest
}: FieldSearchProps) {
    const [translate] = useTranslation(['common']);
    const noInteraction = Boolean(rest.disabled || rest.readOnly || !onChange);

    const closeActionMarkup =
        value && !noInteraction ? (
            <ClearActionWrapper $theme={rest.theme}>
                <ClearAction onClick={() => onChange?.('')}>
                    <ClearIconWrapper>
                        <IconClose />
                    </ClearIconWrapper>
                </ClearAction>
            </ClearActionWrapper>
        ) : null;

    const prefixMarkup = (
        <SearchIconWrapper $theme={rest.theme}>
            <IconSearch />
        </SearchIconWrapper>
    );

    // TODO: We may want to move `AnimatePresence` into the
    // `FieldText > Suffix` container.
    const suffixMarkup = (
        <>
            <AnimatePresence>{closeActionMarkup}</AnimatePresence>
            {suffix}
        </>
    );

    return (
        <FieldText
            ref={wrapperRef}
            type="search"
            value={value}
            placeholder={placeholder || translate('search')}
            prefix={prefixMarkup}
            prefixOffset={PREFIX_OFFSET}
            prefixPassthrough
            suffix={suffixMarkup}
            suffixOffset={SUFFIX_OFFSET + suffixOffset}
            size="small"
            onChange={onChange}
            {...rest}
        />
    );
}
